// OnboardingPage — WebOnboarding from auth-screens.jsx.
// 4-step animated full-screen split: editorial text left, full-bleed photo right.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const IMG = (id: string, w = 1600) =>
  `https://images.unsplash.com/photo-${id}?w=${w}&q=80&auto=format&fit=crop`;

const SLIDES = [
  {
    kicker: 'INTRODUCING GLOBAL FLYT',
    title: ['Travel,', 'rediscovered.'],
    body: "One app for flights, stays, and the AI that does the spreadsheet for you. No fine-print theatre. No 13 tabs.",
    img: IMG('1469854523086-cc02fe5d8800'),
    stat: { v: '47M', l: 'TRAVELERS PLANNED · 2025' },
    location: 'Iceland',
  },
  {
    kicker: '01 · DISCOVER',
    title: ['Worth-it places,', 'picked daily.'],
    body: "A feed curated by people who actually go. Save what you love, share with your group.",
    img: IMG('1507525428034-b723cf961d3e'),
    stat: { v: '2,400+', l: 'DESTINATIONS HAND-CURATED' },
    location: 'Bali',
  },
  {
    kicker: '02 · GLOBAL FLYT AI',
    title: ['Plan in a chat.', 'Book in a tap.'],
    body: "Tell us a vibe, a budget, a vibe again. Get a real itinerary — flights, stays, vibes — bookable in one tap.",
    img: IMG('1488646953014-85cb44e25828'),
    stat: { v: '11s', l: 'AVG TIME TO A FULL TRIP PLAN' },
    location: 'Lisbon',
  },
  {
    kicker: '03 · TRANSPARENT',
    title: ['No fee theatre.', 'Just go.'],
    body: "Real prices, real availability, free cancellation up to 24h. We take a flat 3% — never marked up, never hidden.",
    img: IMG('1605640840605-14ac1855827b'),
    stat: { v: '$0', l: 'HIDDEN FEES · EVER' },
    location: 'Marrakech',
  },
];

const T = {
  bg:      '#0A0F1C',
  ink:     '#F4F3F0',
  inkDim:  '#8C98B0',
  primary: '#E8441A',
  accent:  '#4B8EDB',
  line:    'rgba(255,255,255,0.07)',
  display: '"Inter Variable","Inter",system-ui,sans-serif',
  serif:   '"Fraunces Variable","Fraunces",Georgia,serif',
};

export function OnboardingPage() {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  // step is always clamped to [0, SLIDES.length-1] so this is always defined
  const s = SLIDES[step]!;

  const next = () => {
    if (step < SLIDES.length - 1) {
      setStep(step + 1);
    } else {
      navigate('/login');
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000, background: T.bg,
      overflow: 'hidden', display: 'flex',
    }}>
      {/* LEFT — editorial type column */}
      <div style={{
        flex: '0 0 46%', position: 'relative',
        padding: '56px clamp(16px, 4vw, 64px)',
        display: 'flex', flexDirection: 'column',
        background: T.bg,
      }}>
        {/* Logo + skip */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="32" height="32" viewBox="0 0 32 32">
              <path d="M4 6l8 18 4-9 8 11" stroke="#E8441A" strokeWidth="2.6" strokeLinecap="round" fill="none" strokeLinejoin="round"/>
              <circle cx="26" cy="6" r="3" fill="#E8441A"/>
            </svg>
            <span style={{ fontFamily: T.display, fontSize: 22, fontWeight: 700, letterSpacing: -0.5, color: T.ink }}>
              global flyt<span style={{ color: '#E8441A' }}>.</span>
            </span>
          </div>
          <button onClick={() => navigate('/login')} style={{
            padding: '8px 16px', borderRadius: 999, background: 'transparent',
            color: T.inkDim, border: `1px solid ${T.line}`, cursor: 'pointer',
            fontFamily: T.display, fontSize: 13, fontWeight: 500,
          }}>Skip intro →</button>
        </div>

        <div style={{ flex: 1 }}/>

        {/* Eyebrow */}
        <div key={`k-${step}`} style={{
          fontFamily: T.display, fontSize: 12, letterSpacing: 3, fontWeight: 700,
          color: T.primary, marginBottom: 28,
          animation: 'fade-up .6s cubic-bezier(.2,.7,.2,1.1) both',
        }}>● {s.kicker}</div>

        {/* Title */}
        <h1 key={`t-${step}`} style={{
          fontFamily: T.display, fontSize: 'clamp(64px,7vw,110px)', fontWeight: 600,
          lineHeight: 0.9, margin: 0, letterSpacing: -0.04, color: T.ink,
        }}>
          <span style={{ display: 'block', animation: 'fade-up .7s cubic-bezier(.2,.7,.2,1.1) both' }}>
            {s.title[0]}
          </span>
          <span style={{
            display: 'block', fontFamily: T.serif, fontStyle: 'italic', fontWeight: 400,
            animation: 'fade-up .7s .1s cubic-bezier(.2,.7,.2,1.1) both',
          }}>{s.title[1]}</span>
        </h1>

        {/* Body */}
        <p key={`b-${step}`} style={{
          fontFamily: '"Inter Variable","Inter",system-ui,sans-serif',
          fontSize: 17, lineHeight: 1.55, color: T.inkDim,
          marginTop: 28, maxWidth: 480,
          animation: 'fade-up .7s .2s cubic-bezier(.2,.7,.2,1.1) both',
        }}>{s.body}</p>

        {/* Stat */}
        <div key={`s-${step}`} style={{
          marginTop: 40, padding: '22px 0', borderTop: `1px solid ${T.line}`, borderBottom: `1px solid ${T.line}`,
          maxWidth: 480, display: 'flex', alignItems: 'baseline', gap: 18,
          animation: 'fade-up .7s .3s cubic-bezier(.2,.7,.2,1.1) both',
        }}>
          <span style={{ fontFamily: T.display, fontSize: 56, fontWeight: 700, color: T.ink, letterSpacing: -0.04, lineHeight: 1 }}>{s.stat.v}</span>
          <span style={{ fontFamily: T.display, fontSize: 11, letterSpacing: 1.5, fontWeight: 600, color: T.inkDim }}>{s.stat.l}</span>
        </div>

        <div style={{ flex: 1 }}/>

        {/* Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginTop: 36 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {SLIDES.map((_, i) => (
              <div key={i} onClick={() => setStep(i)} style={{
                height: 4, width: i === step ? 36 : 14,
                background: i === step ? T.ink : T.line,
                borderRadius: 2, transition: 'all .3s', cursor: 'pointer',
              }}/>
            ))}
          </div>
          <div style={{ flex: 1 }}/>
          <span style={{ fontFamily: T.display, fontSize: 12, color: T.inkDim, fontWeight: 500 }}>
            0{step + 1} / 0{SLIDES.length}
          </span>
          <button onClick={next} style={{
            height: 56, padding: '0 28px', borderRadius: 999,
            background: T.ink, color: T.bg, border: 'none', cursor: 'pointer',
            fontFamily: T.display, fontSize: 14, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            {step < SLIDES.length - 1 ? 'Continue' : 'Get started'}
            <span style={{ fontSize: 18 }}>→</span>
          </button>
        </div>
      </div>

      {/* RIGHT — photo column */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {SLIDES.map((sl, i) => (
          <div key={i} style={{
            position: 'absolute', inset: 0,
            opacity: i === step ? 1 : 0,
            transform: i === step ? 'scale(1)' : 'scale(1.06)',
            transition: 'opacity 1s ease, transform 1.4s ease',
          }}>
            <img src={sl.img} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt=""/>
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(0,0,0,0.15), rgba(0,0,0,0.05))' }}/>
          </div>
        ))}

        {/* Photo caption */}
        <div key={`pc-${step}`} style={{
          position: 'absolute', bottom: 36, left: 36, right: 36,
          padding: '18px 22px', borderRadius: 18,
          background: 'rgba(10,12,16,0.55)', backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.1)', color: '#fff',
          display: 'flex', alignItems: 'center', gap: 14,
          animation: 'fade-up .8s .2s cubic-bezier(.2,.7,.2,1.1) both',
        }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #E8441A, #4B8EDB)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: T.bg }}>✦</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: T.display, fontSize: 13, fontWeight: 700, letterSpacing: 0.5 }}>FROM THE FIELD</div>
            <div style={{ fontFamily: '"Inter Variable","Inter",system-ui,sans-serif', fontSize: 12, opacity: 0.85, marginTop: 2 }}>Photographed by our community in {s.location}</div>
          </div>
        </div>

        <div style={{ position: 'absolute', top: 36, right: 36, fontFamily: T.display, fontSize: 13, color: '#fff', opacity: 0.85, letterSpacing: 2, fontWeight: 600, padding: '6px 12px', borderRadius: 999, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(12px)' }}>● LIVE</div>
      </div>
    </div>
  );
}
